import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, ClickAwayListener, Paper, Popper, Stack, TextField, Typography } from '@mui/material';
import { HexColorPicker } from 'react-colorful';

function expandShortHex(hex: string): string | null {
  const t = hex.trim();
  if (/^#[0-9A-Fa-f]{3}$/i.test(t)) {
    return `#${t[1]}${t[1]}${t[2]}${t[2]}${t[3]}${t[3]}`.toLowerCase();
  }
  return null;
}

/** Normalize to #rrggbb lowercase, or null if not a plain hex color. */
export function normalizeHexColor(value: string): string | null {
  const t = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(t)) return t.toLowerCase();
  return expandShortHex(t);
}

/** Value for `<input type="color">` (always 6-digit hex). */
export function hexForNativeColorInput(value: string): string {
  return normalizeHexColor(value) ?? '#808080';
}

const PRESET_COLORS = [
  '#ffffff', '#000000', '#f44336', '#e91e63', '#9c27b0', '#673ab7',
  '#3f51b5', '#2196f3', '#03a9f4', '#00bcd4', '#009688', '#4caf50',
  '#8bc34a', '#cddc39', '#ffeb3b', '#ffc107', '#ff9800', '#ff5722',
  '#795548', '#607d8b', '#00e5ff', '#69f0ae', '#ffea00', '#ff4081',
];

export interface ColorPickerWithPaletteProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
}

/**
 * Compact color picker: swatch button + text field.
 * Clicking the swatch opens a popover with react-colorful picker and preset swatches.
 */
const ColorPickerWithPalette: React.FC<ColorPickerWithPaletteProps> = ({
  label,
  value,
  onChange,
  placeholder,
  helperText,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const [draftValue, setDraftValue] = useState(value);
  const anchorRef = useRef<HTMLDivElement>(null);
  const displayHex = useMemo(() => hexForNativeColorInput(value), [value]);
  const normalizedDraft = useMemo(() => normalizeHexColor(draftValue), [draftValue]);

  useEffect(() => {
    setDraftValue(value);
  }, [value]);

  const handleTextChange = (nextRaw: string) => {
    setDraftValue(nextRaw);
    const normalized = normalizeHexColor(nextRaw);
    if (normalized) onChange(normalized);
  };

  const handleTextBlur = () => {
    // Keep free typing while editing, but don't leave invalid/empty text in place.
    setDraftValue(normalizedDraft ?? value);
  };

  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" color="text.secondary" component="label" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="center" ref={anchorRef}>
        <Box
          onClick={() => !disabled && setOpen((o) => !o)}
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: 1,
            bgcolor: displayHex,
            border: '2px solid',
            borderColor: 'divider',
            cursor: disabled ? 'default' : 'pointer',
            transition: 'box-shadow 0.15s',
            '&:hover': disabled ? {} : { boxShadow: '0 0 0 2px rgba(100,180,255,0.4)' },
          }}
        />
        <TextField
          size="small"
          fullWidth
          value={draftValue}
          onChange={(e) => handleTextChange(e.target.value)}
          onBlur={handleTextBlur}
          placeholder={placeholder}
          helperText={helperText}
          disabled={disabled}
          sx={{
            '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8rem', py: 0.75 },
            '& .MuiFormHelperText-root': { mt: 0.5 },
          }}
        />
      </Stack>

      <Popper open={open && !disabled} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 1500 }}>
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Paper elevation={8} sx={{ p: 1.5, width: 240 }}>
            <HexColorPicker
              color={displayHex}
              onChange={(next) => {
                setDraftValue(next);
                onChange(next);
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Presets
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
              {PRESET_COLORS.map((hex) => (
                <Box
                  key={hex}
                  onClick={() => {
                    setDraftValue(hex);
                    onChange(hex);
                  }}
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '3px',
                    bgcolor: hex,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: hex === displayHex ? 'primary.main' : 'divider',
                    '&:hover': { transform: 'scale(1.2)' },
                    transition: 'transform 0.1s',
                  }}
                />
              ))}
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Stack>
  );
};

export default ColorPickerWithPalette;
